<template>
  <div class="step-needs-root">
    <button
      class="payment-sandbox-close"
      title="Logout"
      @click="logoutAndExit"
    >
      <span aria-hidden="true">&times;</span>
    </button>
    <OnboardingLayout
    :title="$t('onboarding.needs.title')"
    :subtitle="$t('onboarding.needs.subtitle')"
    :can-proceed="true"
    :show-progress="false"
    :show-skip-button="false"
    :next-button-label="$t('onboarding.needs.cta_trial')"
    @next="handleNext"
  >
    <div class="onboarding-needs">
      <!-- Pages slider (pricing-style) -->
      <div class="control-group">
        <label for="pages-value">{{ $t('onboarding.needs.pages_label') }}</label>
        <div class="slider-wrapper">
          <input
            id="pages-slider"
            type="range"
            :min="SLIDER_CONFIG.MIN_PERCENT"
            :max="SLIDER_CONFIG.MAX_PERCENT"
            :step="SLIDER_CONFIG.STEP"
            :value="pagesSliderValue"
            class="slider"
            :style="pagesSliderStyle"
            @input="onPagesSlider($event)"
          />
          <input
            id="pages-value"
            :type="isPagesCustom ? 'text' : 'number'"
            :min="pagesMin"
            :max="pagesMax"
            :value="pagesDisplayValue"
            class="number-input"
            :readonly="isPagesCustom"
            @input="onPagesInput($event)"
          />
        </div>
      </div>

      <!-- Users slider (pricing-style) -->
      <div class="control-group">
        <label for="users-value">{{ $t('onboarding.needs.users_label') }}</label>
        <div class="slider-wrapper">
          <input
            id="users-slider"
            type="range"
            :min="SLIDER_CONFIG.MIN_PERCENT"
            :max="SLIDER_CONFIG.MAX_PERCENT"
            :step="SLIDER_CONFIG.STEP"
            :value="usersSliderValue"
            class="slider"
            :style="usersSliderStyle"
            @input="onUsersSlider($event)"
          />
          <input
            id="users-value"
            :type="isUsersCustom ? 'text' : 'number'"
            :min="usersMin"
            :max="usersMax"
            :value="usersDisplayValue"
            class="number-input"
            :readonly="isUsersCustom"
            @input="onUsersInput($event)"
          />
        </div>
      </div>

      <!-- Dropdown: existing management tool -->
      <div class="needs-select-group">
        <label>{{ $t('onboarding.needs.existing_tool_label') }}</label>
        <div class="needs-select-wrapper" :class="{ open: existingToolOpen }" v-click-outside="closeExistingTool">
          <button type="button" class="needs-select-trigger" @click="existingToolOpen = !existingToolOpen">
            <span :class="{ placeholder: !existingTool }">
              {{ existingTool || $t('onboarding.needs.select_placeholder') }}
            </span>
            <i class="fa-solid fa-chevron-down needs-select-arrow" :class="{ rotated: existingToolOpen }"></i>
          </button>
          <ul v-show="existingToolOpen" class="needs-select-dropdown">
            <li
              v-for="(option, i) in existingToolOptions"
              :key="'tool-' + i"
              :class="{ selected: existingTool === option }"
              @click="selectExistingTool(option)"
            >{{ option }}</li>
          </ul>
        </div>
      </div>

      <!-- Dropdown: how did you hear about us -->
      <div class="needs-select-group">
        <label>{{ $t('onboarding.needs.referral_label') }}</label>
        <div class="needs-select-wrapper" :class="{ open: referralOpen }" v-click-outside="closeReferral">
          <button type="button" class="needs-select-trigger" @click="referralOpen = !referralOpen">
            <span :class="{ placeholder: !referralSource }">
              {{ referralSource || $t('onboarding.needs.select_placeholder') }}
            </span>
            <i class="fa-solid fa-chevron-down needs-select-arrow" :class="{ rotated: referralOpen }"></i>
          </button>
          <ul v-show="referralOpen" class="needs-select-dropdown">
            <li
              v-for="(option, i) in referralOptions"
              :key="'ref-' + i"
              :class="{ selected: referralSource === option }"
              @click="selectReferral(option)"
            >{{ option }}</li>
          </ul>
        </div>
      </div>
    </div>
  </OnboardingLayout>
  </div>
</template>

<script>
import OnboardingLayout from '../OnboardingLayout.vue'
import User from '../../mixins/User.vue'
import { SLIDER_CONFIG } from '../../dash/pricing/constants/slider'
import { getRealValue, getSliderPercent, SCALES } from '../../dash/pricing/utils/scales'
import { getDisplayValue, isCustomValue } from '../../dash/pricing/utils/sliderDisplay'

export default {
  name: 'StepNeeds',

  mixins: [User],

  components: {
    OnboardingLayout
  },

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value()
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  data() {
    return {
      SLIDER_CONFIG,
      pagesValue: 5,
      usersValue: 1,

      // Dropdown state
      existingToolOpen: false,
      referralOpen: false,
      existingTool: '',
      referralSource: '',

      existingToolOptions: [
        this.$t('onboarding.needs.tool_no'),
        'Agorapulse',
        'Brandwatch',
        'Hootsuite',
        'Meltwater',
        'Sprinklr Social',
        'Sprout Social'
      ],
      referralOptions: [
        this.$t('onboarding.needs.referral_social'),
        this.$t('onboarding.needs.referral_search'),
        this.$t('onboarding.needs.referral_recommendation'),
        this.$t('onboarding.needs.referral_event'),
        this.$t('onboarding.needs.referral_ad'),
        this.$t('onboarding.needs.referral_podcast')
      ]
    }
  },

  computed: {
    // Pages slider
    pagesScale() { return SCALES.social },
    pagesMin()   { return this.pagesScale[0][1] },
    pagesMax()   { return this.pagesScale[this.pagesScale.length - 1][1] },
    pagesSliderValue() { return getSliderPercent(this.pagesValue, 'social') },
    pagesSliderStyle() {
      const pct = (this.pagesSliderValue / SLIDER_CONFIG.MAX_PERCENT) * SLIDER_CONFIG.VISUAL_MAX_PERCENT
      return { '--progress-percentage': pct + '%' }
    },
    isPagesCustom()    { return isCustomValue(this.pagesValue) },
    pagesDisplayValue() { return getDisplayValue(this.pagesValue) },

    // Users slider
    usersScale() { return SCALES.users },
    usersMin()   { return this.usersScale[0][1] },
    usersMax()   { return this.usersScale[this.usersScale.length - 1][1] },
    usersSliderValue() { return getSliderPercent(this.usersValue, 'users') },
    usersSliderStyle() {
      const pct = (this.usersSliderValue / SLIDER_CONFIG.MAX_PERCENT) * SLIDER_CONFIG.VISUAL_MAX_PERCENT
      return { '--progress-percentage': pct + '%' }
    },
    isUsersCustom()    { return isCustomValue(this.usersValue) },
    usersDisplayValue() { return getDisplayValue(this.usersValue) }
  },

  mounted() {
    // Restore from store if user navigates back
    const savedPages = this.$store.state.onboarding.needsPages
    const savedUsers = this.$store.state.onboarding.needsUsers
    if (savedPages) this.pagesValue = savedPages
    if (savedUsers) this.usersValue = savedUsers

    // Mark user as actively in onboarding
    this.$store.commit('onboarding/SET_IS_ONBOARDING', true)
  },

  methods: {
    onPagesSlider(event) {
      this.pagesValue = getRealValue(parseFloat(event.target.value), 'social')
    },
    onPagesInput(event) {
      let val = parseInt(event.target.value) || this.pagesMin
      if (val < this.pagesMin) val = this.pagesMin
      if (val > this.pagesMax) val = this.pagesMax
      this.pagesValue = val
    },

    onUsersSlider(event) {
      this.usersValue = getRealValue(parseFloat(event.target.value), 'users')
    },
    onUsersInput(event) {
      let val = parseInt(event.target.value) || this.usersMin
      if (val < this.usersMin) val = this.usersMin
      if (val > this.usersMax) val = this.usersMax
      this.usersValue = val
    },

    // Dropdown helpers
    selectExistingTool(option) {
      this.existingTool = option
      this.existingToolOpen = false
    },
    closeExistingTool() {
      this.existingToolOpen = false
    },
    selectReferral(option) {
      this.referralSource = option
      this.referralOpen = false
    },
    closeReferral() {
      this.referralOpen = false
    },

    handleNext() {
      // Save to store
      this.$store.commit('onboarding/SET_NEEDS', {
        pages: this.pagesValue,
        users: this.usersValue
      })
      this.$store.commit('onboarding/SET_STEP', 1)

      // Navigate to payment step
      this.$router.push({ name: 'onboarding-payment' })
    },

    logoutAndExit() {
      localStorage.clear()
      window.$crisp.push(['do', 'session:reset'])
      location.href = '/'
    }
  }
}
</script>

<style scoped lang="scss">
@import '../variables.scss';

.payment-sandbox-close {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 100;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  color: #9ca3af;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }
}

.onboarding-needs {
  display: flex;
  flex-direction: column;
  gap: 36px;
  max-width: 500px;
}

// ---- Pricing-style slider ----
.control-group {
  display: flex;
  flex-direction: column;

  label {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: $primary-text-color;
    margin-bottom: 15px;
  }

  .slider-wrapper {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .slider {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 10px;
    background: $border-ui;
    border-radius: 5px;
    outline: none;
    margin: 0;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      background: #fff;
      border: 2px solid $tertiary-color;
      border-radius: 50%;
      margin-top: -5px;
      cursor: grab;
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      background: #fff;
      border: 2px solid $tertiary-color;
      border-radius: 50%;
      cursor: grab;
    }

    &::-webkit-slider-runnable-track {
      width: 100%;
      height: 10px;
      background: linear-gradient(
        to right,
        $tertiary-color var(--progress-percentage, 0%),
        $border-ui var(--progress-percentage, 0%)
      );
      border-radius: 5px;
    }

    &::-moz-range-track {
      width: 100%;
      height: 10px;
      background: linear-gradient(
        to right,
        $tertiary-color var(--progress-percentage, 0%),
        $border-ui var(--progress-percentage, 0%)
      );
      border-radius: 5px;
    }

    &::-moz-range-progress {
      background: $tertiary-color;
      height: 10px;
      border-radius: 5px;
    }
  }

  .number-input {
    width: 50px;
    height: 36px;
    border: 1px solid $border-ui;
    border-radius: 8px;
    text-align: center;
    font-size: 14px;
    color: $tertiary-color;
    font-weight: 600;
    box-sizing: border-box;
    -moz-appearance: textfield !important;
    appearance: none;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

// ---- Dropdown selects ----
.needs-select-group {
  display: flex;
  flex-direction: column;

  label {
    font-size: 15px;
    font-weight: 600;
    color: $primary-text-color;
    margin-bottom: 10px;
  }
}

.needs-select-wrapper {
  position: relative;
  width: 100%;
}

.needs-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid $border-ui;
  border-radius: 10px;
  font-size: 14px;
  color: $primary-text-color;
  cursor: pointer;
  transition: border-color 0.2s;
  text-align: left;

  &:hover {
    border-color: $tertiary-color;
  }

  .placeholder {
    color: #9ca3af;
  }
}

.needs-select-arrow {
  font-size: 12px;
  color: #9ca3af;
  transition: transform 0.2s;

  &.rotated {
    transform: rotate(180deg);
  }
}

.needs-select-wrapper.open .needs-select-trigger {
  border-color: $tertiary-color;
}

.needs-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid $border-ui;
  border-radius: 10px;
  list-style: none;
  padding: 6px 0;
  margin: 0;
  z-index: 10;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  max-height: 240px;
  overflow-y: auto;

  li {
    padding: 10px 16px;
    font-size: 14px;
    color: $primary-text-color;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: $bg-ui;
    }

    &.selected {
      background: $bg-ui-selected;
      color: $tertiary-color;
      font-weight: 500;
    }
  }
}
</style>
